import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { validateInvite, setupInviteAccount } from '@/lib/api';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Loader2, Lock, User, MapPin, CheckCircle2, AlertTriangle, Eye, EyeOff } from 'lucide-react';

export default function InviteSetupPage({ inviteToken: propToken }) {
  const params = useParams();
  const navigate = useNavigate();
  const token = propToken || params.token;
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [alreadySetup, setAlreadySetup] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  // Quick login state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPass, setLoginPass] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  const handleQuickLogin = async () => {
    setLoginError('');
    setLoginLoading(true);
    try {
      const { data } = await api.post('/auth/email/login', { email: loginEmail, password: loginPass });
      if (data.session_token) {
        sessionStorage.setItem('session_token', data.session_token);
        localStorage.setItem('session_token', data.session_token);
      }
      window.location.href = data.role === 'mr' ? '/mr-mobile' : '/dashboard';
    } catch (e) {
      setLoginError(e?.response?.data?.detail || 'Wrong email or password');
    } finally { setLoginLoading(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await validateInvite(token);
        if (data.already_setup) {
          // Account exists — show login form pre-filled
          setAlreadySetup(true);
          setLoginEmail(data.email || '');
          setInvite(data);
        } else {
          setInvite(data);
        }
      } catch (e) {
        const msg = e?.response?.data?.detail || 'Invalid or expired invite link';
        setError(msg);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleSetup = async () => {
    if (password.length < 6) return setError('Password must be at least 6 characters');
    if (password !== confirmPassword) return setError('Passwords do not match');
    setError(null);
    setSaving(true);
    try {
      const { data } = await setupInviteAccount(token, { password });
      if (data.session_token) {
        sessionStorage.setItem('session_token', data.session_token);
        localStorage.setItem('session_token', data.session_token);
      }
      setDone(true);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Setup failed. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #f0fdf9 0%, #e8f5f0 50%, #f3f7f5 100%)' }}>
      <Card className="w-full max-w-md shadow-xl border-0" data-testid="invite-setup-card">
        <CardContent className="p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-2xl bg-emerald-600 flex items-center justify-center mx-auto mb-4">
              <span className="text-white text-xl font-black">P</span>
            </div>
            <h1 className="text-2xl font-bold text-foreground"><img src="/logo-final-e.svg" alt="Pharm-X" className="h-8 w-auto object-contain" /></h1>
            <p className="text-sm text-muted-foreground mt-1">Field Representative Setup</p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
            </div>
          ) : done ? (
            <div className="text-center py-8" data-testid="setup-success">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-foreground mb-2">You're all set!</h2>
              <p className="text-sm text-muted-foreground mb-6">Your account is ready. Open your field dashboard below.</p>
              <Button
                onClick={() => {
                  window.location.href = '/mr-mobile';
                }}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-lg py-6"
                data-testid="open-dashboard-btn"
              >
                Open My Dashboard
              </Button>
            </div>
          ) : alreadySetup ? (
            <div className="space-y-5" data-testid="invite-login-view">
              <div className="text-center">
                <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                  <CheckCircle2 className="w-7 h-7 text-emerald-600" />
                </div>
                <h2 className="text-lg font-bold text-foreground mb-1">Welcome back{invite?.name ? `, ${invite.name}` : ''}!</h2>
                <p className="text-sm text-muted-foreground">Log in with your password to continue</p>
              </div>

              <div className="space-y-3">
                <div>
                  <Label htmlFor="loginEmail">Email</Label>
                  <Input
                    id="loginEmail"
                    type="email"
                    value={loginEmail}
                    readOnly
                    className="bg-muted"
                    data-testid="invite-login-email"
                  />
                </div>
                <div>
                  <Label htmlFor="loginPass">Password</Label>
                  <div className="relative">
                    <Input
                      id="loginPass"
                      type={showPass ? 'text' : 'password'}
                      value={loginPass}
                      onChange={e => setLoginPass(e.target.value)}
                      placeholder="Enter your password"
                      data-testid="invite-login-password"
                      onKeyDown={e => e.key === 'Enter' && handleQuickLogin()}
                    />
                    <button type="button" onClick={() => setShowPass(p => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                      {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                {loginError && <p className="text-sm text-red-500 text-center">{loginError}</p>}
                <Button
                  onClick={handleQuickLogin}
                  disabled={loginLoading || !loginEmail || !loginPass}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  data-testid="invite-login-btn"
                >
                  {loginLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
                  Log In
                </Button>
              </div>
            </div>
          ) : error && !invite ? (
            <div className="text-center py-8" data-testid="invite-error">
              <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3">
                <AlertTriangle className="w-7 h-7 text-red-500" />
              </div>
              <h2 className="text-lg font-bold text-foreground mb-1">Invalid Link</h2>
              <p className="text-sm text-muted-foreground mb-4">{error}</p>
              <Button
                onClick={() => { window.location.href = '/login?type=signin'; }}
                variant="outline"
                className="mx-auto"
              >
                Go to Login
              </Button>
            </div>
          ) : invite ? (
            <div className="space-y-6" data-testid="invite-setup-form">
              {/* Welcome */}
              <div className="bg-emerald-50 dark:bg-emerald-950/20 rounded-xl p-4 border border-emerald-200 dark:border-emerald-800">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center text-white font-bold text-sm">
                    {invite.name?.[0]?.toUpperCase() || 'M'}
                  </div>
                  <div>
                    <div className="font-semibold text-foreground flex items-center gap-2">
                      <User className="w-3.5 h-3.5 text-muted-foreground" /> {invite.name}
                    </div>
                    {invite.state && (
                      <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                        <MapPin className="w-3 h-3" /> {invite.state}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <p className="text-sm text-muted-foreground text-center">
                Create a password to access your field dashboard
              </p>

              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPass ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Min 6 characters"
                    className="pl-10 pr-10"
                    data-testid="invite-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(p => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type={showPass ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter password"
                    className="pl-10"
                    data-testid="invite-confirm-password-input"
                  />
                </div>
              </div>

              {error && (
                <div className="text-sm text-red-500 text-center" data-testid="setup-error">{error}</div>
              )}

              <Button
                onClick={handleSetup}
                disabled={saving || !password || !confirmPassword}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                data-testid="invite-setup-btn"
              >
                {saving ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Setting up...</>
                ) : (
                  'Activate Account'
                )}
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
